import { createSlice } from '@reduxjs/toolkit'
import lizzyApi from 'lizzyApiAdapter';

const dummySuggestion = 'AI-generated legal documents are often more accurate and consistent than those created manually, reducing the risk of errors and inconsistencies. Overall, AI-generated content has the potential to revolutionize the legal industry, making legal processes faster, more efficient, and more accessible to individuals and businesses alike';

const toolbarSlice = createSlice({
  name: 'toolbar',
  initialState: {
    isOpen: false,
    isDisplayed: false,
    hasChanges: false,
    isConfirmationModalOpen: false,
    isExpanded: false,
    suggestion: '',
    suggestionStream: [],
    isWriting: false,
    currentRange: null,
    currentBounds: null,
    activeItem: '',
    chatMessage: '',
    isShortcutOpen: false,
    isWidgetOpen: false,
    writingTimeout: null,
    selectedText: '',
    itemStatus: {
      summarize: {
        active: false
      },
      autocomplete: {
        active: false
      },
      sidebar: {
        active: false
      }
    },
  },
  reducers: {
    toggleItem(state, { payload }) {
      if (state.itemStatus[payload.itemKey]) {
        state.itemStatus[payload.itemKey].active = !state.itemStatus[payload.itemKey].active;
      }
    },
    activateItem(state, { payload }) {
      state.activeItem = payload;
    },
    setIsOpen(state, { payload }) {
      state.isOpen = payload;
    },
    setIsDisplayed(state, { payload }) {
      state.isDisplayed = payload;
    },
    setHasChanges(state, { payload }) {
      state.hasChanges = payload;
    },
    setIsConfirmationModalOpen(state, { payload }) {
      state.isConfirmationModalOpen = payload;
    },
    setIsExpanded(state, { payload }) {
      state.isExpanded = payload;
    },
    setIsWriting(state, { payload }) {
      state.isWriting = payload;
    },
    setSuggestion(state, { payload }) {
      state.suggestion = payload;
    },
    setCurrentRange(state, { payload }) {
      state.currentRange = payload;
    },
    setCurrentBounds(state, { payload }) {
      state.currentBounds = payload;
    },
    setChatMessage(state, { payload }) {
      state.chatMessage = payload;
    },
    closeToolbar(state) {
      state.isWriting && lizzyApi.stopGenerate();

      state.isOpen = false;
      state.isWriting = false;
      state.isDisplayed = false;
      state.suggestion = '';
      state.chatMessage = '';
      state.hasChanges = false;
      state.activeItem = '';
      state.isExpanded = false;
      state.currentBounds = null;
      state.isShortcutOpen = false;
      state.writingTimeout && clearTimeout(state.writingTimeout);
      state.suggestionStream = [];
      state.selectedText = '';
    },
    clearActiveData(state) {
      state.suggestion = '';
      state.chatMessage = '';
      state.activeItem = '';
    },
    setIsShortcutOpen(state, { payload }) {
      state.isShortcutOpen = payload;
    },
    setIsWidgetOpen(state, { payload }) {
      state.isWidgetOpen = payload;
    },
    setWritingTimeout(state, { payload }) {
      state.writingTimeout = payload;
    },
    addSuggestionStream(state, { payload }) {
      state.suggestionStream.push(payload);
    },
    clearSuggestionStream(state, { payload }) {
      state.suggestionStream = [];
    },
    setSelectedText(state, { payload }) {
      state.selectedText = payload;
    },
  }
});

export const {
  toggleItem,
  setIsOpen,
  setHasChanges,
  setIsConfirmationModalOpen,
  setIsExpanded,
  setIsWriting,
  setSuggestion,
  setCurrentRange,
  setCurrentBounds,
  setIsDisplayed,
  activateItem,
  setChatMessage,
  closeToolbar,
  clearActiveData,
  setIsShortcutOpen,
  setIsWidgetOpen,
  setWritingTimeout,
  addSuggestionStream,
  setSelectedText,
  clearSuggestionStream,
} = toolbarSlice.actions;
export const toolbarReducer = toolbarSlice.reducer;

export default toolbarSlice;

export const summarizeText = (text) => (dispatch, getState) => {
  dispatch(setIsWriting(true));
  dispatch(setSuggestion(''));

  // demo only, replace with api
  const writingTimeout = getState().toolbar.writingTimeout;
  writingTimeout && clearTimeout(writingTimeout);

  const timeout = setTimeout(() => {
    dispatch(setSuggestion(dummySuggestion));
    dispatch(setIsWriting(false));
  }, 2000);

  dispatch(setWritingTimeout(timeout));
}

export const rephraseCurrent = () => (dispatch, getState) => {
  dispatch(setIsWriting(true));
  dispatch(setSuggestion(''));

  // demo only, replace with api
  const writingTimeout = getState().toolbar.writingTimeout;
  writingTimeout && clearTimeout(writingTimeout);

  const timeout = setTimeout(() => {
    dispatch(setSuggestion('Rephrased ' + dummySuggestion));
    dispatch(setIsWriting(false));
  }, 2000);

  dispatch(setWritingTimeout(timeout));
}

export const continueWritingCurrent = () => (dispatch, getState) => {
  dispatch(setIsWriting(true));

  // demo only, replace with api
  const writingTimeout = getState().toolbar.writingTimeout;
  writingTimeout && clearTimeout(writingTimeout);

  const timeout = setTimeout(() => {
    const nextSuggestion = getState().toolbar.suggestion + ' ' + dummySuggestion;

    dispatch(setSuggestion(nextSuggestion));
    dispatch(setIsWriting(false));
  }, 2000);

  dispatch(setWritingTimeout(timeout));
}

export const shorterCurrent = () => (dispatch, getState) => {
  dispatch(setIsWriting(true));

  // demo only, replace with api
  const writingTimeout = getState().toolbar.writingTimeout;
  writingTimeout && clearTimeout(writingTimeout);

  const timeout = setTimeout(() => {
    const suggestion = getState().toolbar.suggestion;
    const nextSuggestion = suggestion.substr(0, suggestion.length - 60);

    dispatch(setSuggestion(nextSuggestion));
    dispatch(setIsWriting(false));
  }, 2000);

  dispatch(setWritingTimeout(timeout));
};

export const longerCurrent = () => (dispatch, getState) => {
  dispatch(setIsWriting(true));

  // demo only, replace with api
  const writingTimeout = getState().toolbar.writingTimeout;
  writingTimeout && clearTimeout(writingTimeout);

  const timeout = setTimeout(() => {
    const suggestion = getState().toolbar.suggestion;
    const nextSuggestion = suggestion + ' ' + dummySuggestion.substr(0, 60);

    dispatch(setSuggestion(nextSuggestion));
    dispatch(setIsWriting(false));
  }, 2000);

  dispatch(setWritingTimeout(timeout));
};

export const continueWriting = (text) => async (dispatch, getState) => {
  await dispatch(setSuggestion(text));

  dispatch(setIsWriting(true));

  // demo only, replace with api
  const writingTimeout = getState().toolbar.writingTimeout;
  writingTimeout && clearTimeout(writingTimeout);

  const timeout = setTimeout(() => {
    const suggestion = getState().toolbar.suggestion;
    const nextSuggestion = suggestion + ' ' + dummySuggestion.substr(0, 60);

    dispatch(setSuggestion(nextSuggestion));
    dispatch(setIsWriting(false));
  }, 2000);

  dispatch(setWritingTimeout(timeout));
};

export const generateClause = () => async (dispatch, getState) => {
  dispatch(activateItem(''));
  dispatch(setIsExpanded(true));
  dispatch(summarizeText(''));
};

export const askLizzy = (prompt, selectedText) => (dispatch, getState) => {
  dispatch(generateResponse('generateTextResponse', { prompt, selectedText }));
}

export const summarizeHandler = (selectedText) => async (dispatch, getState) => {
  dispatch(generateResponse('generateSummaries', { selectedText }));
};

export const continueWritingHandler = (selectedText, options) => async (dispatch, getState) => {
  dispatch(generateResponse('continueWriting', { selectedText }, options));
};

export const makeShorterHandler = (selectedText) => async (dispatch, getState) => {
  dispatch(generateResponse('makeShorter', { selectedText }));
};

export const makeLongerHandler = (selectedText) => async (dispatch, getState) => {
  dispatch(generateResponse('makeLonger', { selectedText }));
};

export const rephraseHandler = (selectedText) => async (dispatch, getState) => {
  dispatch(generateResponse('rephrase', { selectedText }));
};

export const generateResponse = (action, { selectedText, prompt }, options = {}) => (dispatch, getState) => {
  dispatch(startGenerateText(options));

  lizzyApi.run(action)({ selectedText, prompt }, (result) => {
    if (result.valid && !lizzyApi.isStop) {
      dispatch(addSuggestionStream(result.text));
      dispatch(joinStremToSuggestionText());
      dispatch(setHasChanges(true));
    }

    if (result.eof || lizzyApi.isStop) {
      dispatch(setIsWriting(false));
    }
  });
};

export const restartCurrentAction = () => (dispatch) => {
  dispatch(startGenerateText());
  lizzyApi.restartLastAction();
};

export const stopGenerate = () => (dispatch) => {
  lizzyApi.stopGenerate();
  dispatch(setIsWriting(false));
};

export const startGenerateText = (options = {}) => async (dispatch) => {
  dispatch(setIsWriting(true));
  dispatch(setIsExpanded(true));

  if (!options.continue) {
    dispatch(clearActiveData());
    dispatch(clearSuggestionStream());
  }
};

export const joinStremToSuggestionText = () => (dispatch, getState) => {
  dispatch(setSuggestion(getState().toolbar.suggestionStream.join('')));
}